<template>
  <v-card tile>
    <v-card-text class="mt-12">
      <v-row class="mt-8">
        <v-col cols="12">
          <v-chip label color="transparent" text-color="black">
            <h2>Cuentas Corrientes</h2>
          </v-chip>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-alert color="info" dense outlined>
            <v-icon left color="red">mdi-alert-circle</v-icon>
            <strong>Saldo en rojo:</strong> Deuda del jugador
            <v-icon left color="green" class="ml-4">mdi-check-circle</v-icon>
            <strong>Saldo en verde:</strong> Saldo a favor del jugador
          </v-alert>
        </v-col>
      </v-row>
      <v-row class="mt-2">
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="filtros.apellidos"
            prepend-icon="mdi-account"
            label="Apellidos"
            dense
            clearable
            @keypress.enter="buscarCuentasCorrientes"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="filtros.nombres"
            prepend-icon="mdi-account"
            label="Nombres"
            dense
            clearable
            @keypress.enter="buscarCuentasCorrientes"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <v-select
            v-model="filtros.ordenSaldo"
            :items="opcionesOrden"
            label="Ordenar por saldo"
            dense
            prepend-icon="mdi-sort"
          ></v-select>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-btn
            color="primary"
            @click="buscarCuentasCorrientes"
            :loading="loading"
          >
            Buscar
            <v-icon right>mdi-magnify</v-icon>
          </v-btn>
          <v-btn color="grey" class="ml-2" @click="limpiarFiltros">
            Limpiar
          </v-btn>
          <v-btn color="error" class="ml-2" @click="abrirAgregarSaldoDeudor">
            <v-icon left>mdi-plus</v-icon>
            Agregar Saldo Deudor
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-text v-if="cuentasCorrientes.length > 0">
      <v-data-table
        :headers="headers"
        :items="cuentasCorrientes"
        class="elevation-1"
        locale="es-ar"
        item-key="idPaciente"
        :items-per-page="10"
        :server-items-length="cantRegistros"
        :footer-props="{
          itemsPerPageText: 'Filas por página',
          itemsPerPageOptions: [5, 10, 15, 20],
        }"
        :options.sync="options"
        :loading="loading"
      >
        <template v-slot:item.saldo="{ item }">
          <v-chip
            :color="
              item.saldo > 0 ? 'error' : item.saldo < 0 ? 'success' : 'grey'
            "
            dark
            small
          >
            ${{ Math.abs(item.saldo.toFixed(2)) }}
          </v-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                medium
                color="primary"
                class="mr-3"
                v-on="on"
                v-bind="attrs"
                @click="verDetalle(item)"
              >
                mdi-eye
              </v-icon>
            </template>
            <span>Ver detalle</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                medium
                color="success"
                class="mr-3"
                v-on="on"
                v-bind="attrs"
                @click="cancelarDeuda(item)"
              >
                mdi-cash-check
              </v-icon>
            </template>
            <span>Cancelar deuda</span>
          </v-tooltip>
        </template>
      </v-data-table>
    </v-card-text>
    <v-card-text v-else-if="!loading && busquedaRealizada">
      <v-alert color="purple" dark outlined>
        No se encontraron cuentas corrientes con los filtros aplicados.
      </v-alert>
    </v-card-text>

    <DetalleCuentaCorriente
      :visible="showDetalle"
      :idPaciente="pacienteSeleccionado"
      @close="cierraDetalle"
    ></DetalleCuentaCorriente>
    <CancelarDeudaCuentaCorriente
      :visible="showCancelarDeuda"
      :idPaciente="pacienteSeleccionado"
      :saldoDeudor="saldoDeudor"
      :jugadorNombre="jugadorNombre"
      @close="cierraCancelarDeuda"
      @pagado="handlePagado"
    ></CancelarDeudaCuentaCorriente>
    <AgregarSaldoDeudor
      :visible="showAgregarSaldoDeudor"
      @close="cierraAgregarSaldoDeudor"
      @agregado="handleAgregado"
    ></AgregarSaldoDeudor>
  </v-card>
</template>

<script>
import axios from "@/axios-auth";
import { showAlert } from "@/utils/toast";
import DetalleCuentaCorriente from "./DetalleCuentaCorriente.vue";
import CancelarDeudaCuentaCorriente from "./CancelarDeudaCuentaCorriente.vue";
import AgregarSaldoDeudor from "./AgregarSaldoDeudor.vue";

export default {
  components: {
    DetalleCuentaCorriente,
    CancelarDeudaCuentaCorriente,
    AgregarSaldoDeudor,
  },
  data() {
    return {
      filtros: {
        apellidos: "",
        nombres: "",
        ordenSaldo: "desc",
      },
      opcionesOrden: [
        { text: "Mayor a menor", value: "desc" },
        { text: "Menor a mayor", value: "asc" },
      ],
      cuentasCorrientes: [],
      headers: [
        { text: "Apellidos", value: "apellidos" },
        { text: "Nombres", value: "nombres" },
        { text: "Saldo", value: "saldo" },
        { text: "Acciones", value: "actions", sortable: false },
      ],
      loading: false,
      busquedaRealizada: false,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      cantRegistros: 0,
      showDetalle: false,
      pacienteSeleccionado: 0,
      showCancelarDeuda: false,
      saldoDeudor: 0,
      jugadorNombre: "",
      showAgregarSaldoDeudor: false,
    };
  },
  watch: {
    options: {
      deep: true,
      handler(val) {
        if (this.busquedaRealizada && Object.keys(val).length) {
          this.buscarCuentasCorrientes();
        }
      },
    },
  },
  methods: {
    buscarCuentasCorrientes() {
      this.loading = true;
      this.busquedaRealizada = true;
      const params = {
        apellidos: this.filtros.apellidos,
        nombres: this.filtros.nombres,
        ordenSaldo: this.filtros.ordenSaldo,
        idInstitucion: this.$store.state.usuario.institucion.idInstitucion,
        page: this.options.page,
        items: this.options.itemsPerPage,
      };

      let self = this;
      axios
        .get("/Caja/GetCuentasCorrientes", { params })
        .then(function (response) {
          if (response.data && response.data.listaCuentas) {
            self.cuentasCorrientes = response.data.listaCuentas;
            self.cantRegistros = response.data.cantRegistros;
          } else {
            self.cuentasCorrientes = [];
            self.cantRegistros = 0;
          }
          self.loading = false;
        })
        .catch(function (error) {
          self.loading = false;
          self.showAlert({
            icon: "error",
            title: "Error al buscar cuentas corrientes.",
            vm: self,
            timer: 2000,
          });
        });
    },
    cargarFiltrosDesdeQuery() {
      const query = this.$route.query;
      if (query.apellidos) {
        this.filtros.apellidos = query.apellidos;
      }
      if (query.nombres) {
        this.filtros.nombres = query.nombres;
      }
      if (query.buscar === "true") {
        this.buscarCuentasCorrientes();
      }
    },
    limpiarFiltros() {
      this.filtros.apellidos = "";
      this.filtros.nombres = "";
      this.filtros.ordenSaldo = "desc";
      this.cuentasCorrientes = [];
      this.busquedaRealizada = false;
      this.options.page = 1;
    },
    verDetalle(item) {
      this.pacienteSeleccionado = item.idPaciente;
      this.showDetalle = true;
    },
    cancelarDeuda(item) {
      this.pacienteSeleccionado = item.idPaciente;
      this.saldoDeudor = item.saldo;
      this.jugadorNombre = `${item.apellidos}, ${item.nombres}`;
      this.showCancelarDeuda = true;
    },
    cierraCancelarDeuda() {
      this.showCancelarDeuda = false;
    },
    handlePagado() {
      this.showCancelarDeuda = false;
      this.buscarCuentasCorrientes();
    },
    abrirAgregarSaldoDeudor() {
      this.showAgregarSaldoDeudor = true;
    },
    cierraAgregarSaldoDeudor() {
      this.showAgregarSaldoDeudor = false;
    },
    handleAgregado() {
      this.showAgregarSaldoDeudor = false;
      this.buscarCuentasCorrientes();
    },
    cierraDetalle() {
      this.showDetalle = false;
      this.buscarCuentasCorrientes();
    },
    showAlert(alertInfo) {
      showAlert(alertInfo);
    },
  },
  mounted() {
    this.cargarFiltrosDesdeQuery();
  },
};
</script>
